import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Collapse,
  Flex,
  Heading,
  List,
  ListIcon,
  ListItem,
  Skeleton,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  MdArrowBack,
  MdCheckCircleOutline,
  MdErrorOutline,
  MdExpandLess,
  MdExpandMore,
  MdPhotoCamera,
  MdStop,
} from 'react-icons/md';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { headingFont, typography } from 'theme/tokens';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead } from '../_shared/readState';
import CameraAimFrame from './components/CameraAimFrame';
import { useCameraLiveViewFrame } from './hooks/useCameraLiveViewFrame';
import { useCameraLiveViewSession } from './hooks/useCameraLiveViewSession';
import { useGetOrganizationCourts } from './hooks/useGetOrganizationCourts';
import { useListVenueAgents } from './hooks/useListVenueAgents';
import { useScreenWakeLock } from './hooks/useScreenWakeLock';
import {
  AimCameraOption,
  AimViewDisplay,
  FrameAgeTone,
  VENUE_AGENTS_PATH,
  aimCameraHeader,
  aimCameraOptions,
  aimPagePath,
  aimViewDisplay,
  aimt,
  describeAimBlocker,
  describeFrameAge,
  describeLiveViewError,
  frameAgeMs,
  parseAimMac,
  sameMac,
} from './utils/camera-aim.utils';

/** The list read only feeds the switcher (`connected`, court names); the picture never waits on it. */
const AGENT_LIST_POLL_MS = 30_000;
/** Moves the age badge and the "box is late" check between two frame answers. */
const CLOCK_TICK_MS = 1_000;

const TIP_KEYS = ['tip_position', 'tip_centred', 'tip_players', 'tip_court'] as const;

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Warm semantic pill (DESIGN.md "Status badges"): tone text on its 12 % tint. */
function AgeBadge({ tone, label }: { tone: FrameAgeTone; label: string }) {
  // A shade darker on light surfaces: this is read outdoors, on a phone.
  const shade = useColorModeValue('600', '500');
  const palette: Record<FrameAgeTone, { color: string; bg: string }> = {
    fresh: { color: `success.${shade}`, bg: 'rgba(16, 185, 129, 0.12)' },
    late: { color: `warning.${shade}`, bg: 'rgba(196, 136, 58, 0.12)' },
    frozen: { color: `danger.${shade}`, bg: 'rgba(184, 69, 58, 0.12)' },
  };
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
      h="28px"
      px={3}
      borderRadius="full"
      fontFamily={headingFont}
      fontSize="13px"
      fontWeight="600"
      whiteSpace="nowrap"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
      color={palette[tone].color}
      bg={palette[tone].bg}
      data-testid="aim-age-badge"
      data-tone={tone}
    >
      {label}
    </Box>
  );
}

/** What the 16:9 frame says while it has no picture to show. */
function FrameMessage({ display }: { display: AimViewDisplay }) {
  let title: string;
  let hint: string | null = null;
  switch (display.kind) {
    case 'starting':
      title = aimt('starting');
      hint = aimt('starting_hint');
      break;
    case 'box-late':
      title = aimt('box_late');
      break;
    case 'blocked':
      title = describeAimBlocker(display.blocker);
      break;
    case 'ended':
      title = aimt('ended_title');
      hint = display.capped ? aimt('ended_capped') : aimt('ended_hint');
      break;
    case 'replaced':
      title = aimt('replaced_title');
      hint = aimt('replaced_hint');
      break;
    case 'error':
      title = describeLiveViewError(display.error);
      break;
    default:
      title = aimt('opening');
  }
  const isProblem = display.kind === 'box-late' || display.kind === 'error' || display.kind === 'blocked';
  return (
    <Box data-testid="aim-frame-message" data-kind={display.kind}>
      {isProblem ? (
        <MdErrorOutline size={28} style={{ margin: '0 auto 6px' }} />
      ) : (
        <MdPhotoCamera size={28} style={{ margin: '0 auto 6px' }} />
      )}
      <Text fontSize="sm" fontWeight="600">
        {title}
      </Text>
      {hint && (
        <Text fontSize="xs" mt={1}>
          {hint}
        </Text>
      )}
    </Box>
  );
}

function CameraSwitcher({
  options,
  currentMac,
  onSwitch,
}: {
  options: AimCameraOption[];
  /** The camera on screen; `null` when the link names none. */
  currentMac: string | null;
  onSwitch: (mac: string) => void;
}) {
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box mt={5}>
      <Text {...typography.formLabel} color={muted} mb={2}>
        {aimt('cameras_label')}
      </Text>
      <Flex gap={2} wrap="wrap" data-testid="aim-camera-switcher">
        {options.map((option) => {
          const isCurrent = sameMac(option.mac, currentMac);
          // A phone shows no `title` tooltip: a camera that cannot be aimed
          // says why in place of its MAC tail.
          const blocked = !isCurrent && option.blocker !== null;
          return (
            <Button
              key={option.mac}
              size="md"
              minH="44px"
              borderRadius="full"
              variant={isCurrent ? 'solid' : 'outline'}
              colorScheme={isCurrent ? 'brand' : undefined}
              aria-pressed={isCurrent}
              isDisabled={blocked}
              title={option.blocker ? describeAimBlocker(option.blocker) : undefined}
              onClick={() => {
                if (!isCurrent) onSwitch(option.mac);
              }}
              data-testid={`aim-camera-${option.mac}`}
            >
              {option.courtLabel}
              <Text
                as="span"
                ml={2}
                fontFamily={blocked ? undefined : 'mono'}
                fontSize="xs"
                opacity={0.8}
              >
                {blocked && option.blocker ? describeAimBlocker(option.blocker) : option.tail}
              </Text>
            </Button>
          );
        })}
      </Flex>
    </Box>
  );
}

function MountingTips() {
  const tips = useDisclosure();
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  return (
    <Box mt={5} pt={2} borderTopWidth="1px" borderColor={border}>
      <Button
        variant="ghost"
        size="md"
        w="100%"
        minH="44px"
        px={2}
        justifyContent="space-between"
        rightIcon={tips.isOpen ? <MdExpandLess /> : <MdExpandMore />}
        aria-expanded={tips.isOpen}
        onClick={tips.onToggle}
      >
        {aimt('tips_title')}
      </Button>
      <Collapse in={tips.isOpen} animateOpacity>
        <List spacing={2} mt={2} px={2} data-testid="aim-tips">
          {TIP_KEYS.map((key) => (
            <ListItem key={key} fontSize="sm" display="flex" alignItems="flex-start">
              <ListIcon as={MdCheckCircleOutline} color="brand.500" mt="2px" />
              {aimt(key)}
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

/**
 * The camera aim view (docs/epics/camera-aim-live-view.md): one camera's
 * picture, refreshed about once a second, for a club admin on a ladder with
 * a phone in one hand.
 *
 * Phone-first and one-handed: the picture fills the width (and never more
 * than the screen's height, for landscape), its age sits right above it, and
 * every action is a full-width, 48 px button under it. The screen is kept
 * awake while the page is visible.
 *
 * The page renders; the decisions live in `utils/camera-aim.utils.ts`, the
 * lease in `useCameraLiveViewSession`, the picture in `useCameraLiveViewFrame`.
 * Route: `#/admin/court-recording/aim/:agentId?camera=<MAC>`.
 */
export default function CameraAimPage() {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  // The camera to aim is the link's, in the form the API takes — NOT the
  // box's camera row, which a reboot drops for a heartbeat or two: gating
  // the session on the row would end it (and black the picture out) then.
  const cameraMac = parseAimMac(searchParams.get('camera'));
  const navigate = useNavigate();
  const muted = useColorModeValue('gray.500', 'gray.400');
  const now = useNow(CLOCK_TICK_MS);

  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.id || '';

  const { data: agents, isInitialLoading, isError } = useListVenueAgents(orgId, {
    refetchIntervalMs: AGENT_LIST_POLL_MS,
  });
  const listState = classifyRead(isError, isInitialLoading, agents);
  const agent = useMemo(
    () => (agents ?? []).find((candidate) => candidate.id === agentId) ?? null,
    [agents, agentId],
  );
  const { courts } = useGetOrganizationCourts();
  const nameById = useMemo(() => new Map(courts.map((court) => [court.id, court.name])), [courts]);
  const options = useMemo(
    () => (agent ? aimCameraOptions(agent, nameById, now) : []),
    [agent, nameById, now],
  );
  // The route param reaches a URL only once it names an agent the API listed
  // for this organization — never a path segment typed into the address bar.
  const canAim = listState === 'answered' && agent !== null && cameraMac !== null;

  const session = useCameraLiveViewSession({
    agentId: agent?.id ?? '',
    organizationId: orgId,
    mac: canAim ? cameraMac : '',
    enabled: canAim,
  });
  const frame = useCameraLiveViewFrame({
    agentId: agent?.id ?? '',
    organizationId: orgId,
    sessionId: session.phase === 'active' ? session.sessionId : null,
    enabled: canAim,
  });
  useScreenWakeLock(true);

  // The relay saw another session, or went empty: ask now, not in ≤ 15 s.
  const { checkNow } = session;
  useEffect(() => {
    if (frame.sessionSuspect) checkNow();
  }, [frame.sessionSuspect, checkNow]);

  const [stopping, setStopping] = useState(false);
  const goBack = (): void => navigate(VENUE_AGENTS_PATH);
  const switchTo = (mac: string): void => navigate(aimPagePath(agentId, mac), { replace: true });
  const handleStop = async (): Promise<void> => {
    setStopping(true);
    await session.stop();
    navigate(VENUE_AGENTS_PATH);
  };

  const backLink = (
    <Button
      variant="ghost"
      size="sm"
      px={2}
      mb={2}
      leftIcon={<MdArrowBack />}
      onClick={goBack}
      data-testid="aim-back"
    >
      {aimt('back')}
    </Button>
  );

  if (!canAim) {
    let body;
    if (listState === 'never-issued') body = <ScopePrompt gate="organization" />;
    else if (listState === 'loading') body = <Skeleton w="100%" h="220px" borderRadius="md" />;
    else if (listState === 'unreadable')
      body = (
        <Text role="alert" fontSize="sm">
          {aimt('list_error')}
        </Text>
      );
    else if (!agent)
      body = (
        <Text role="alert" fontSize="sm" data-testid="aim-agent-not-found">
          {aimt('agent_not_found')}
        </Text>
      );
    else
      body = (
        <>
          <Text role="alert" fontSize="sm" data-testid="aim-camera-invalid">
            {aimt('camera_invalid')}
          </Text>
          <CameraSwitcher options={options} currentMac={null} onSwitch={switchTo} />
        </>
      );
    return (
      <Box maxW="960px" mx="auto" w="100%">
        {backLink}
        <Card p={{ base: 3, md: 6 }}>{body}</Card>
      </Box>
    );
  }

  const camera = aimCameraHeader(agent, cameraMac, options, nameById, now);
  const display = aimViewDisplay({
    phase: session.phase,
    state: session.state,
    receivedAt: session.receivedAt,
    hasFrame: frame.imageUrl !== null,
    endedByCap: session.endedByCap,
    error: session.error,
    blocker: camera.blocker,
    now,
  });
  const age = display.kind === 'live' ? describeFrameAge(frameAgeMs(frame.anchor, now)) : null;

  let action;
  if (display.kind === 'ended' || display.kind === 'replaced') {
    action = (
      <Button
        size="lg"
        w={{ base: '100%', sm: 'auto' }}
        colorScheme="brand"
        onClick={() => void session.start()}
      >
        {display.kind === 'ended' ? aimt('continue') : aimt('take_over')}
      </Button>
    );
  } else if (display.kind === 'error') {
    // A refusal the admin cannot fix from here gets no retry: only the back link.
    action =
      display.error === 'not-found' || display.error === 'unavailable' ? (
        <Button
          size="lg"
          w={{ base: '100%', sm: 'auto' }}
          colorScheme="brand"
          onClick={() => void session.start()}
        >
          {aimt('retry')}
        </Button>
      ) : null;
  } else {
    action = (
      <Button
        size="lg"
        w={{ base: '100%', sm: 'auto' }}
        variant="outline"
        leftIcon={<MdStop />}
        isLoading={stopping}
        onClick={() => void handleStop()}
      >
        {aimt('stop')}
      </Button>
    );
  }

  return (
    <Box maxW="960px" mx="auto" w="100%">
      {backLink}
      <Card p={{ base: 3, md: 6 }}>
        <Flex alignItems="flex-start" justifyContent="space-between" gap={3} mb={3}>
          <Box minW={0}>
            <Heading as="h1" {...typography.sectionTitle} fontSize={{ base: 'xl', md: '2xl' }} noOfLines={1}>
              {camera.courtLabel ?? aimt('camera_tail', { tail: camera.tail })}
            </Heading>
            {camera.courtLabel !== null && (
              <Text fontSize="sm" fontFamily="mono" color={muted} data-testid="aim-camera-tail">
                {aimt('camera_tail', { tail: camera.tail })}
              </Text>
            )}
          </Box>
          {age && <AgeBadge tone={age.tone} label={age.label} />}
        </Flex>

        <CameraAimFrame
          imageUrl={display.kind === 'live' ? frame.imageUrl : null}
          alt={aimt('picture_alt', { camera: cameraMac })}
        >
          <FrameMessage display={display} />
        </CameraAimFrame>

        {action && (
          <Flex mt={3} gap={2} direction={{ base: 'column', sm: 'row' }}>
            {action}
          </Flex>
        )}

        {options.some((option) => !sameMac(option.mac, cameraMac)) && (
          <CameraSwitcher options={options} currentMac={cameraMac} onSwitch={switchTo} />
        )}

        <MountingTips />
      </Card>
    </Box>
  );
}
